/**
 * Client tasks store (ENG-2845) — backs the dashboard Agenda sidebar.
 *
 * Holds the signed-in user's client-audience tasks from /api/{user}/tasks/.
 * The flag check lives in the fetch action (not just the UI) so nothing ever
 * calls the API for a user outside the rollout; a 403 from the backend (flag
 * off server-side) degrades to an empty list rather than an error banner.
 */
import { fetchTasks, reopenTask, saveTaskAnswers, submitTask } from '@/services/tasks';
import { FLAG_CLIENT_TASKS, isFeatureEnabled } from '@/store/feature_flags';

export const OPEN_TASK_STATUSES = ['open', 'in_progress'];

// Due-date ordering: dated tasks first (soonest on top), undated ones keep
// their creation order after them.
const byDueDate = (a, b) => {
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    return (a.created || '').localeCompare(b.created || '');
};

export default {
    namespaced: true,
    state: {
        items: [],
        loaded: false,
        loading: false,
    },
    mutations: {
        setItems(state, items) {
            state.items = items || [];
            state.loaded = true;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        updateItem(state, task) {
            const index = state.items.findIndex(item => item.uuid === task.uuid);
            if (index === -1) {
                state.items.push(task);
            } else {
                state.items.splice(index, 1, task);
            }
        },
    },
    getters: {
        tasks: state => state.items,
        loading: state => state.loading,
        loaded: state => state.loaded,
        openTasks: state => state.items.filter(task => OPEN_TASK_STATUSES.includes(task.status)).sort(byDueDate),
        completedTasks: state => state.items.filter(task => ['submitted', 'completed'].includes(task.status)),
        // Trust names for the Agenda project filter — only trusts that actually
        // have tasks show up.
        projects: (state, getters) => [...new Set(getters.openTasks.map(task => task.trust_name).filter(Boolean))],
        // Closed tasks whose definition opted into client re-opening — the
        // serializer only sets reopen_cta on those (O2's "Add more asset info").
        reopenableTasks: state => state.items.filter(task => task.reopen_cta),
    },
    actions: {
        async fetch({ commit, rootGetters }) {
            if (!isFeatureEnabled(FLAG_CLIENT_TASKS)) return;
            const userId = rootGetters.user?.uuid;
            if (!userId) return;
            commit('setLoading', true);
            try {
                commit('setItems', await fetchTasks(userId));
            } catch (error) {
                // 403 = flag off server-side; anything else degrades the same
                // way — the sidebar simply doesn't render stale mock content.
                commit('setItems', []);
            } finally {
                commit('setLoading', false);
            }
        },
        async saveAnswers({ commit, rootGetters }, { taskUuid, answers }) {
            const updated = await saveTaskAnswers(rootGetters.user?.uuid, taskUuid, answers);
            commit('updateItem', updated);
            return updated;
        },
        async submit({ commit, rootGetters }, { taskUuid, answers = null }) {
            const updated = await submitTask(rootGetters.user?.uuid, taskUuid, answers);
            commit('updateItem', updated);
            return updated;
        },
        async reopen({ commit, state, rootGetters }, { taskUuid }) {
            const updated = await reopenTask(rootGetters.user?.uuid, taskUuid);
            commit('updateItem', updated);
            // A repeat definition answers with a FRESH row: the task we posted
            // against stays closed, and the server now suppresses its
            // reopen_cta because an open sibling exists — but our copy still
            // carries the CTA. Clear it locally so the dashboard doesn't show
            // a stale "Report another gift" beside the open task. Only the
            // clicked row can have carried it (the serializer puts the repeat
            // CTA on one row at most), so clearing it alone is complete.
            if (updated.uuid !== taskUuid) {
                const parent = state.items.find(task => task.uuid === taskUuid);
                if (parent && parent.reopen_cta) {
                    commit('updateItem', { ...parent, reopen_cta: null });
                }
            }
            return updated;
        },
    },
};
